import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload, Trash2, FileText, ImageIcon, Music, Video, File, Download, Copy, Check } from "lucide-react";
import axios from "axios";
import { formatApiError, API, authHeaders } from "@/hooks/useAdminData";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function getFileIcon(contentType) {
  if (contentType?.startsWith("image/")) return ImageIcon;
  if (contentType?.startsWith("audio/")) return Music;
  if (contentType?.startsWith("video/")) return Video;
  if (contentType?.includes("pdf")) return FileText;
  return File;
}

function formatSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FileManager({ files, loadData }) {
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/admin-files/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data", ...authHeaders() },
      });
      loadData();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const deleteFile = async (id) => {
    if (!window.confirm("Delete this file?")) return;
    await axios.delete(`${API}/admin-files/${id}`, { withCredentials: true, headers: authHeaders() }).catch(() => {});
    loadData();
  };

  const copyLink = (downloadUrl) => {
    const fullUrl = `${window.location.origin}${downloadUrl}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopiedId(downloadUrl);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Uploaded Files ({files.length})
        </h3>
        <div>
          <Label htmlFor="file-upload" className="cursor-pointer">
            <div className="btn-secondary text-sm inline-flex items-center gap-1 px-4 py-2 rounded-full font-semibold" data-testid="upload-file-btn">
              <Upload size={14} /> {uploading ? "Uploading..." : "Upload File"}
            </div>
          </Label>
          <Input
            id="file-upload"
            type="file"
            className="hidden"
            onChange={handleUpload}
            disabled={uploading}
            data-testid="file-upload-input"
          />
        </div>
      </div>
      <p className="text-[#4A4A4A] text-xs mb-4">
        Upload documents, images, audio, or video files (max 50MB). Copy the link to share anywhere.
      </p>
      <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]">Type</TableHead>
              <TableHead>File Name</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="w-[120px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {files.map(f => {
              const Icon = getFileIcon(f.content_type);
              const fullUrl = f.download_url;
              return (
                <TableRow key={f.id}>
                  <TableCell>
                    {f.content_type?.startsWith("image/") ? (
                      <img src={fullUrl} alt={f.original_filename} className="w-10 h-8 object-cover rounded" />
                    ) : (
                      <Icon size={20} className="text-[#E8922F]" />
                    )}
                  </TableCell>
                  <TableCell className="font-medium text-sm max-w-[200px] truncate">{f.original_filename}</TableCell>
                  <TableCell className="text-xs text-gray-500">{formatSize(f.size)}</TableCell>
                  <TableCell className="text-xs text-gray-500">{new Date(f.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="text-[#E8922F] hover:text-[#C4392D] p-1" title="Download" data-testid={`download-file-${f.id}`}>
                        <Download size={15} />
                      </a>
                      <button
                        onClick={() => copyLink(f.download_url)}
                        className="text-gray-400 hover:text-[#1A1A1A] p-1" title="Copy link"
                        data-testid={`copy-link-${f.id}`}
                      >
                        {copiedId === f.download_url ? <Check size={15} className="text-green-500" /> : <Copy size={15} />}
                      </button>
                      <button onClick={() => deleteFile(f.id)} className="text-red-400 hover:text-red-600 p-1" title="Delete" data-testid={`delete-file-${f.id}`}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {files.length === 0 && <p className="text-center text-gray-400 py-8 text-sm">No files uploaded yet.</p>}
      </div>
    </>
  );
}
